import { useSelector } from 'react-redux';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { logout } from '@lootboy/lib/actions/AuthActions';
import {
  ERegisterUserDetailsFormField,
  TRegisterUserDetailsFieldValues,
} from '@lootboy/lib/forms/useRegisterUserDeatilsForm';
import { registrationNotCompletedSelector } from '@lootboy/lib/selectors';
import { TGender } from '@lootboy/lib/types/user';

import ButtonLink from '~/components/atoms/ButtonLink';
import { Divider } from '~/components/atoms/Divider';
import { DateInput } from '~/components/atoms/FormControls/DateInput';
import Input from '~/components/atoms/FormControls/Input';
import { Select } from '~/components/atoms/FormControls/Select';
import { ScrollContainer } from '~/components/atoms/ScrollContainer';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import { MenuViewHeader } from '~/components/MenuViewHeader';

import useBooleanState from '~/hooks/useBooleanState';
import { useClosePopup } from '~/hooks/useClosePopup';
import { MenuOption } from '~/popups/MenuPopup/MenuOptionsGroup/MenuOption';
import { useUserDetails } from '~/popups/UserDetailsPopup/useUserDetails';
import {
  userHasPasswordSelector,
  userRecruitedSelector,
} from '~/redux/selectors/user';
import { TAppStore, useAppDispatch } from '~/redux/store/useAppDispatch';
import EnsureLoggedInContainer from '~/routes/EnsureLoggedInRoute/EnsureLoggedInContainer';
import { CHANGE_PASSWORD_PATH, USER_DETAILS_PATH } from '~/routes/paths';
import { useAppLocation } from '~/utilities/routing';

import { ReactComponent as CheckIcon } from '~/assets/images/iconsLibrary/check-circle.svg';
import { ReactComponent as ChevronIcon } from '~/assets/images/iconsLibrary/chevron-left.svg';
import { ReactComponent as EditIcon } from '~/assets/images/iconsLibrary/edit.svg';

import { DeleteAccount } from './DeleteAccount';
import { RecruiterPopup } from './RecruiterPopup';
import { ResetChatRadar } from './ResetChatRadar';

import styles from './UserDetailView.module.scss';

const FORM_ID = 'user_detail_form';

export function UserDetailsPopup(): JSX.Element {
  const { state } = useAppLocation<typeof USER_DETAILS_PATH>();
  const closePopup = useClosePopup();
  const dispatch = useAppDispatch();
  const registrationNotCompleted = useSelector<TAppStore, boolean>(
    registrationNotCompletedSelector,
  );
  const isUserRecruited = useSelector(userRecruitedSelector);
  const doesUserHavePassword = useSelector(userHasPasswordSelector);
  const [
    isRecruiterOpen,
    { setTrue: openRecruiter, setFalse: closeRecruiter },
  ] = useBooleanState(!!state?.openRecruiter && !isUserRecruited);

  const { form, selectOptions } = useUserDetails();

  return (
    <EnsureLoggedInContainer>
      <MenuViewHeader
        title={I18n.t('MenuView_profile_bc')}
        onClose={() => {
          if (registrationNotCompleted) {
            dispatch(logout());
            return;
          }
          closePopup();
        }}
      />
      {isRecruiterOpen && <RecruiterPopup close={closeRecruiter} />}
      <ScrollContainer flex>
        <section className={styles.content}>
          <h2 className={styles.headline}>
            {registrationNotCompleted
              ? I18n.t('MenuView_profile_headline')
              : I18n.t('MenuView_profile_edit_headline')}
          </h2>
          <form
            onSubmit={form.onSubmit}
            id={FORM_ID}
          >
            <Input
              {...form.register(ERegisterUserDetailsFormField.USERNAME)}
              label={I18n.t('MenuView_profile_username')}
              placeholder={I18n.t('MenuView_profile_username_placeholder')}
              id={ERegisterUserDetailsFormField.USERNAME}
              error={
                form.errors[ERegisterUserDetailsFormField.USERNAME]?.message
              }
              icon={
                registrationNotCompleted ? undefined : (
                  <EditIcon className={styles.editIcon} />
                )
              }
            />
            {!registrationNotCompleted && (
              <div className={styles.security}>
                <span className={styles.securityHeadline}>
                  {I18n.t('MenuView_profile_security')}
                </span>
                <ButtonLink
                  to={CHANGE_PASSWORD_PATH}
                  className={styles.changePasswordLink}
                >
                  {doesUserHavePassword
                    ? I18n.t('MenuView_change_password')
                    : I18n.t('MenuView_set_password')}
                  <ChevronIcon className={styles.chevronIcon} />
                </ButtonLink>
              </div>
            )}
            <Select<TRegisterUserDetailsFieldValues, TGender>
              label={I18n.t('MenuView_profile_gender')}
              id={ERegisterUserDetailsFormField.GENDER}
              options={selectOptions.gender}
              name={ERegisterUserDetailsFormField.GENDER}
              control={form.control}
              placeholder={I18n.t('MenuView_profile_gender_placeholder')}
              error={form.errors[ERegisterUserDetailsFormField.GENDER]?.message}
              selectProps={{
                isSearchable: false,
              }}
            />
            <DateInput
              control={form.control}
              name={ERegisterUserDetailsFormField.BIRTH_DATE}
              id={ERegisterUserDetailsFormField.BIRTH_DATE}
              placeholder={I18n.t('MenuView_profile_birthdate_placeholder')}
              label={I18n.t('MenuView_profile_birthdate')}
              error={
                form.errors[ERegisterUserDetailsFormField.BIRTH_DATE]?.message
              }
              datePickerProps={{
                maxDate: new Date(),
              }}
            />
            <Select
              label={I18n.t('MenuView_profile_country')}
              id={ERegisterUserDetailsFormField.COUNTRY}
              control={form.control}
              name={ERegisterUserDetailsFormField.COUNTRY}
              options={selectOptions.country}
              error={
                form.errors[ERegisterUserDetailsFormField.COUNTRY]?.message
              }
              selectProps={{
                filterOption: (option, inputValue) => {
                  const name = (
                    option.data as { name: string }
                  ).name.toLowerCase();
                  return name.startsWith(inputValue.toLowerCase());
                },
              }}
            />
            <Divider className={classNames(styles.divider, styles.top)} />
            {registrationNotCompleted ? (
              <MenuOption
                label={
                  isUserRecruited
                    ? I18n.t('MenuView_invite_success')
                    : I18n.t('MenuView_profile_invite_bpoint')
                }
                onClick={() => {
                  if (!isUserRecruited) {
                    openRecruiter();
                  }
                }}
                className={classNames(
                  styles.recruiter,
                  isUserRecruited && styles.recruited,
                )}
                labelClassName={styles.recruiterLabel}
              >
                {isUserRecruited ? (
                  <CheckIcon className={styles.checkIcon} />
                ) : null}
              </MenuOption>
            ) : (
              <>
                <ResetChatRadar />
                <DeleteAccount />
              </>
            )}
            <Divider className={classNames(styles.divider, styles.bottom)} />
          </form>
        </section>
      </ScrollContainer>
      <footer className={styles.footer}>
        <StyledButton
          variant={EStyledButtonVariant.BLUE}
          className={styles.footerButton}
          form={FORM_ID}
          type="submit"
          disabled={!form.isSubmitEnabled}
        >
          {registrationNotCompleted
            ? I18n.t('confirm')
            : I18n.t('MenuView_save_profile')}
        </StyledButton>
      </footer>
    </EnsureLoggedInContainer>
  );
}
